import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@breakingthelines/design-system";

import { cookieLogin, cookieLogout } from "@/lib/auth-api";
import { resolveSessionUser } from "@/lib/auth-session";
import { useAuthStore } from "@/stores/auth-store";
import LogoMark from "@/assets/logo-mark.svg?url";

import styles from "./login-page.module.css";

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

type LoginValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const clearSession = useAuthStore((state) => state.clearSession);
  const setSession = useAuthStore((state) => state.setSession);
  const setLoading = useAuthStore((state) => state.setLoading);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: LoginValues) {
    setSubmitError(null);
    setLoading(true);
    try {
      const loginResult = await cookieLogin(values.email, values.password);
      const user = await resolveSessionUser(loginResult.access_token);

      if (!user) {
        await cookieLogout(loginResult.access_token);
        clearSession();
        setSubmitError("Your account does not have admin access.");
        return;
      }

      setSession({
        user,
        session: {
          token: loginResult.access_token,
          expiresAt: loginResult.expires_at,
        },
      });

      const redirectTo =
        (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/";
      navigate(redirectTo, { replace: true });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Invalid email or password");
      clearSession();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <img alt="Breaking The Lines" src={LogoMark} className={styles.logo} />
      <section className={styles.panel}>
        <p className={styles.title}>Sign in</p>

        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className={styles.fieldStack}>
            {submitError ? (
              <span className={styles.error} role="alert">
                {submitError}
              </span>
            ) : null}
            <div className={styles.field}>
              <div className={styles.inputShell}>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="Enter your email address"
                  autoComplete="email"
                  aria-label="Email address"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? "login-email-error" : undefined}
                  className={styles.input}
                  {...register("email")}
                />
              </div>
              {errors.email ? (
                <span id="login-email-error" className={styles.error} role="status">
                  {errors.email.message}
                </span>
              ) : null}
            </div>

            <div className={styles.field}>
              <div className={styles.inputShell}>
                <Input
                  id="login-password"
                  type="password"
                  placeholder="Enter your password..."
                  autoComplete="current-password"
                  aria-label="Password"
                  aria-invalid={!!errors.password}
                  aria-describedby={errors.password ? "login-password-error" : undefined}
                  className={styles.input}
                  {...register("password")}
                />
              </div>
              {errors.password ? (
                <span id="login-password-error" className={styles.error} role="status">
                  {errors.password.message}
                </span>
              ) : null}
            </div>
          </div>

          <div className={styles.actions}>
            <Button type="submit" disabled={isSubmitting} className={styles.primaryButton}>
              {isSubmitting ? "Signing in…" : "Sign In"}
            </Button>
            <button type="button" className={styles.supportLink}>
              Reset your password
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
